import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { useMapboxMapContext } from '../contexts/MapboxMapContext'
import { DeadEnd } from '@/shared/api/routes'

export interface UseCampsiteDiscoveryMarkersProps {
  deadEnds: DeadEnd[]
  selectedDeadEnd?: DeadEnd | null
  onDeadEndClick?: (deadEnd: DeadEnd) => void
}

/**
 * Hook for displaying custom icon markers for campsite discovery (dead ends)
 * Shows TentTree icon for dead ends and ArrowRightLeft icon for turns
 */
export function useCampsiteDiscoveryMarkers({
  deadEnds = [],
  selectedDeadEnd,
  onDeadEndClick
}: UseCampsiteDiscoveryMarkersProps) {
  const { map, isLoaded, styleLoaded } = useMapboxMapContext()
  const markersRef = useRef<mapboxgl.Marker[]>([])
  const popupsRef = useRef<Map<string, mapboxgl.Popup>>(new Map())
  const onDeadEndClickRef = useRef(onDeadEndClick)
  
  // Store callback in ref to avoid recreating markers when callback changes
  useEffect(() => {
    onDeadEndClickRef.current = onDeadEndClick
  }, [onDeadEndClick])

  // Helper function to format distance
  const formatDistance = (distance?: number) => {
    if (!distance) return ''
    if (distance < 1000) return `${Math.round(distance)}m`
    return `${(distance / 1000).toFixed(1)}km`
  }

  // Helper function to create popup HTML from dead end data
  const createPopupHTML = (deadEnd: DeadEnd): string => {
    const candidate = deadEnd.candidate
    let html = `<div class="p-2 min-w-[200px] max-w-[300px]">
      <div class="font-semibold text-sm mb-2">${deadEnd.roadName || 'Unnamed Road'}</div>
      <div class="text-xs text-gray-600 space-y-1">`

    // Distance from main road
    if (deadEnd.distanceFromMainRoad) {
      html += `<div><span class="font-medium">Distance:</span> ${formatDistance(deadEnd.distanceFromMainRoad)} from main road</div>`
    }

    // Score
    if (candidate?.totalScore !== undefined) {
      html += `<div><span class="font-medium">Score:</span> ${Math.round(candidate.totalScore)}/100</div>`
    }

    // Water Body
    if (candidate?.waterBody) {
      const waterName = candidate.waterBody.name || candidate.waterBody.type
      html += `<div><span class="font-medium">💧 Water:</span> ${waterName} (${formatDistance(candidate.waterBody.distanceMeters)})</div>`
    }

    // Land Ownership
    if (candidate?.landOwnership) {
      html += `<div><span class="font-medium">🏞️ Land:</span> ${candidate.landOwnership.designation}`
      if (candidate.landOwnership.unitName) {
        html += ` - ${candidate.landOwnership.unitName}`
      }
      if (candidate.landOwnership.campingAllowed !== undefined) {
        const status = candidate.landOwnership.campingAllowed ? 'Camping allowed' : 'Check restrictions'
        const color = candidate.landOwnership.campingAllowed ? 'green' : 'orange'
        html += ` <span style="color: ${color};">(${status})</span>`
      }
      html += `</div>`
    }

    // MVUM Status
    if (candidate?.mvumStatus) {
      const statusEmoji = candidate.mvumStatus.status === 'open' ? '✅' : 
                         candidate.mvumStatus.status === 'restricted' ? '⚠️' : 
                         candidate.mvumStatus.status === 'closed' ? '❌' : '❓'
      html += `<div><span class="font-medium">🛣️ MVUM:</span> ${statusEmoji} ${candidate.mvumStatus.status}</div>`
    }

    // Terrain
    if (candidate?.terrain) {
      const terrainParts: string[] = []
      if (candidate.terrain.meanElevation !== null) {
        terrainParts.push(`Elevation: ${Math.round(candidate.terrain.meanElevation)}m`)
      }
      if (candidate.terrain.slopeDegrees !== null && candidate.terrain.slopeDegrees > 0) {
        terrainParts.push(`Slope: ${candidate.terrain.slopeDegrees.toFixed(1)}°`)
      }
      if (terrainParts.length > 0) {
        html += `<div><span class="font-medium">⛰️ Terrain:</span> ${terrainParts.join(' • ')}</div>`
      }
    }

    // Surface Type
    if (candidate?.surface) {
      html += `<div><span class="font-medium">🛤️ Surface:</span> ${candidate.surface}</div>`
    }

    html += `</div></div>`
    return html
  }

  useEffect(() => {
    if (!map.current || !isLoaded || !styleLoaded) {
      // Clear markers if map not ready
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
      return
    }

    // Clear existing markers
    markersRef.current.forEach(marker => {
      try {
        marker.remove()
      } catch (e) {
        // Ignore errors if marker was already removed
      }
    })
    markersRef.current = []

    // Create custom icon markers for each dead end
    deadEnds.forEach((deadEnd, index) => {
      const [lng, lat] = deadEnd.coordinates

      if (lat == null || lng == null || isNaN(Number(lat)) || isNaN(Number(lng))) {
        return
      }

      // Check if this dead end is selected by comparing coordinates and roadId
      const isSelected = selectedDeadEnd && 
        selectedDeadEnd.roadId === deadEnd.roadId &&
        Math.abs(selectedDeadEnd.coordinates[0] - deadEnd.coordinates[0]) < 1e-5 &&
        Math.abs(selectedDeadEnd.coordinates[1] - deadEnd.coordinates[1]) < 1e-5

      // Determine icon and color based on classification
      const isTurn = deadEnd.classification === 'turn'
      
      // TentTree icon path for campsites (dead ends) - lucide-react TentTree icon
      const tentTreeIconPath = '<circle cx="4" cy="4" r="2"></circle><path d="m14 5 3-3 3 3"></path><path d="m14 10 3-3 3 3"></path><path d="M17 14V2"></path><path d="M17 14H7l-5 8h20Z"></path><path d="M8 14v8"></path><path d="m9 14 5 8"></path>'
      
      // ArrowRightLeft icon path for turns - lucide-react ArrowRightLeft icon
      const turnIconPath = '<path d="m16 3 4 4-4 4"></path><path d="M20 7H4"></path><path d="m8 21-4-4 4-4"></path><path d="M4 17h16"></path>'
      
      const iconPath = isTurn ? turnIconPath : tentTreeIconPath

      // Create marker element with icon
      const markerEl = document.createElement('div')
      markerEl.className = 'campsite-discovery-marker'
      
      // Different sizes for selected vs normal
      const iconSize = isSelected ? 28 : 24
      const borderWidth = isSelected ? 3 : 2
      const shadowSize = isSelected ? '0 4px 8px rgba(0,0,0,0.4)' : '0 2px 4px rgba(0,0,0,0.3)'
      // Yellow background for turns, green for dead ends (campsites)
      const backgroundColor = isTurn ? '#facc15' : '#10b981'
      
      markerEl.innerHTML = `
        <div style="
          width: ${iconSize}px;
          height: ${iconSize}px;
          background-color: ${backgroundColor};
          border-radius: 50%;
          border: ${borderWidth}px solid white;
          box-shadow: ${shadowSize};
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease-in-out;
        ">
          <svg width="${iconSize - 8}px" height="${iconSize - 8}px" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            ${iconPath}
          </svg>
        </div>
      `
      
      // Add click handler
      markerEl.addEventListener('click', (e) => {
        e.stopPropagation()
        e.preventDefault()
        if (onDeadEndClickRef.current) {
          onDeadEndClickRef.current(deadEnd)
        }
      })

      // Create marker
      const marker = new mapboxgl.Marker({
        element: markerEl,
        anchor: 'center'
      })
        .setLngLat([lng, lat])
        .addTo(map.current!)

      // Add hover popup
      const markerKey = `${deadEnd.roadId}-${index}`
      let hoverPopup: mapboxgl.Popup | null = null

      markerEl.addEventListener('mouseenter', () => {
        if (!map.current) return

        // Remove any existing popup for this marker
        const existingPopup = popupsRef.current.get(markerKey)
        if (existingPopup) {
          existingPopup.remove()
        }

        // Create new hover popup
        hoverPopup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          anchor: 'bottom',
          offset: [0, -10],
          className: 'campsite-discovery-popup'
        })
          .setLngLat([lng, lat])
          .setHTML(createPopupHTML(deadEnd))
          .addTo(map.current)

        popupsRef.current.set(markerKey, hoverPopup)
      })

      markerEl.addEventListener('mouseleave', () => {
        const popup = popupsRef.current.get(markerKey)
        if (popup) {
          popup.remove()
          popupsRef.current.delete(markerKey)
        }
        hoverPopup = null
      })

      markersRef.current.push(marker)
    })

    return () => {
      // Remove all popups
      popupsRef.current.forEach(popup => {
        try {
          popup.remove()
        } catch (e) {
          // Ignore errors if popup was already removed
        }
      })
      popupsRef.current.clear()

      // Remove all markers
      markersRef.current.forEach(marker => {
        try {
          marker.remove()
        } catch (e) {
          // Ignore errors if marker was already removed
        }
      })
      markersRef.current = []
    }
  }, [map, isLoaded, styleLoaded, deadEnds, selectedDeadEnd])
}

